/**
 * Zone-chart + consistency-display knobs: bin width, divergence-band
 * selection, map-gradient contrast, and the speed/speed-delta chart axes.
 *
 * Tuning any value here: edit → `npm run build` in src/PitBrain.Shared →
 * restart/rebuild the clients.
 */

/**
 * Unit: metres. Default: 5. Bin width shared by the divergence band, map
 * gradient, curve-% row and flat-valley detection — the display layer's
 * spatial resolution. Changing it changes every 5 m-bin-derived number.
 */
export const SAMPLE_INTERVAL = 5

/**
 * Unit: bins. Default: 2. A divergence band never shrinks below this many
 * bins — a single-bin peak is widened to the floor. RAISE to require a
 * wider minimum band; LOWER (to 1) to allow single-bin bands.
 */
export const MIN_BAND_BINS = 2

/**
 * Unitless fraction, in (0, 1). Default: 0.6. Bins qualify for extension
 * from the divergence band's peak while their spread stays ≥ this fraction
 * of the peak spread. LOWER to extend the band further from the peak;
 * RAISE to keep the band tighter around the peak.
 */
export const BAND_EXTENT_RATIO = 0.6

/**
 * Unitless fraction, in (0, 1]. Default: 0.3. The divergence band never
 * exceeds this fraction of the displayBounds span (MIN_BAND_BINS wins in
 * very short zones). LOWER to cap bands tighter; RAISE to allow wider
 * bands.
 */
export const MAX_BAND_FRACTION = 0.3

/**
 * Unitless exponent > 0. Default: 0.8. Monotonic contrast curve applied to
 * the map zone gradient's normalized spread; never moves the argmax bin
 * (the reddest spot always matches the divergence band's peak bin). LOWER
 * toward 0 to compress contrast (flatter colours); RAISE above 1 to punch
 * up contrast toward the peak.
 */
export const GRADIENT_CONTRAST_EXPONENT = 0.8

/**
 * Unitless opacity, in (0, 1). Default: 0.35. Applied to a dimmed (outlier)
 * lap line on the zone chart. Outliers are dimmed, never highlighted — the
 * pack stays full-weight so the eye reads what the rider usually does.
 * LOWER to dim outliers further; RAISE toward 1 to make them less faint.
 */
export const DIMMED_LAP_OPACITY = 0.35

/**
 * Unit: km/h. Default: 15. The speed-delta chart's y-axis never shrinks
 * below ±this: a tight zone must render as a flat braid, not inflated
 * noise. LOWER to allow the axis to zoom in further on tight zones; RAISE
 * to keep more headroom at rest.
 */
export const SPEED_DELTA_AXIS_FLOOR_KMH = 15

/**
 * Unit: km/h. Default: 5. The speed-delta axis expands in these steps once
 * the data exceeds SPEED_DELTA_AXIS_FLOOR_KMH. LOWER for finer axis
 * granularity; RAISE for coarser steps.
 */
export const SPEED_DELTA_AXIS_STEP_KMH = 5
